/**
 * AuthGuard — protects routes that require an authenticated user.
 *
 * Behaviour:
 *   - While /me/ is in flight  → renders <FullPageSkeleton />.
 *   - On success (user found)  → dispatches `setUser` + renders <Outlet />.
 *   - On error (401 / network) → dispatches `clearUser` + redirects to /login.
 *
 * The `useMeQuery` hook is the single source of truth for auth status on the
 * web app.  The Redux auth slice is kept in sync so the rest of the UI can
 * read `state.auth.user` synchronously without issuing further API calls.
 */
import { useEffect } from 'react';
import { Outlet, Navigate } from 'react-router-dom';

import { useMeQuery } from './api/authApi';
import { setUser, clearUser } from './authSlice';
import { useAppDispatch } from '@/app/hooks';
import { FullPageSkeleton } from '@/shared/components/FullPageSkeleton';

export function AuthGuard() {
  const dispatch = useAppDispatch();
  const { data: user, isLoading, isSuccess, isError } = useMeQuery();

  // Keep Redux slice in sync with the RTK Query cache result.
  useEffect(() => {
    if (isSuccess && user) {
      dispatch(setUser(user));
    }
    if (isError) {
      // Token expired / no session — reset auth state to idle.
      dispatch(clearUser());
    }
  }, [dispatch, isSuccess, isError, user]);

  if (isLoading) {
    return <FullPageSkeleton />;
  }

  if (isError || !user) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

export default AuthGuard;
